import { IndicatorSeries, PolygonType, getSupportedIndicators, registerIndicator } from 'klinecharts'
import type { IndicatorFigure, IndicatorTemplate, KLineData } from 'klinecharts'
import { engineBase } from './engineName'
import { defaultInputs, fromCalcParams, toCalcParams } from './inputs'
import { calcLoaderFor, defKey, resolveDef } from './registry'
import type { CalcFn, CalcOutput, IndicatorDef, OutputSpec, Row } from './types'

/**
 * The one place the library meets klinecharts: a def + its calc become an
 * IndicatorTemplate registered under the def's engine name. klinecharts then
 * draws, legends and styles it like any built-in. Inputs travel as positional
 * `calcParams` (schema order) so overrideIndicator({ calcParams }) and the
 * Workbench's replay tail-calc memo keep working unchanged.
 */

/**
 * Column output → klinecharts rows; a key is omitted where the column is NaN
 * (legends test `typeof === 'number'`). A marker column (candle patterns)
 * becomes two keys: `<key>_bull`, a dot just under the bar's low when the signal
 * is positive, and `<key>_bear`, a dot just over its high when negative.
 */
export function toRows(out: CalcOutput, outputs: readonly OutputSpec[], bars: readonly KLineData[]): Row[] {
  const len = bars.length
  const rows: Row[] = new Array<Row>(len)
  const pad = outputs.some((o) => o.plot === 'marker') ? markerPad(bars) : 0
  for (let i = 0; i < len; i++) {
    const row: Row = {}
    for (const o of outputs) {
      const col = out[o.key]
      const v = col ? col[i] : NaN
      if (v !== v) continue
      if (o.plot === 'marker') {
        if (v > 0) row[`${o.key}_bull`] = bars[i].low - pad
        else if (v < 0) row[`${o.key}_bear`] = bars[i].high + pad
      } else {
        row[o.key] = v
      }
    }
    rows[i] = row
  }
  return rows
}

/** A marker dot's distance from the bar: 0.3 × the mean bar range, one number per calc so the dots sit at a consistent distance. */
export function markerPad(bars: readonly KLineData[]): number {
  if (bars.length === 0) return 0
  let sum = 0
  for (const b of bars) sum += b.high - b.low
  return (0.3 * sum) / bars.length
}

/** Mirrors the built-in MACD histogram: colour by sign (user `styles.bars[k]` first), Stroke while growing. */
function histogramFigure(out: OutputSpec, barIndex: number): IndicatorFigure<Row> {
  const base = out.baseValue ?? 0
  return {
    key: out.key,
    title: `${out.label}: `,
    type: 'bar',
    baseValue: base,
    styles: ({ prev, current }, indicator, defaultStyles) => {
      const prevV = prev.indicatorData?.[out.key] ?? Number.MIN_SAFE_INTEGER
      const curV = current.indicatorData?.[out.key] ?? Number.MIN_SAFE_INTEGER
      const own = indicator.styles?.bars?.[barIndex]
      const dflt = defaultStyles.bars[barIndex] ?? defaultStyles.bars[0]
      let color: string
      if (curV > base) color = own?.upColor ?? dflt.upColor
      else if (curV < base) color = own?.downColor ?? dflt.downColor
      else color = own?.noChangeColor ?? dflt.noChangeColor
      return { style: prevV < curV ? PolygonType.Stroke : PolygonType.Fill, color, borderColor: color }
    },
  }
}

/** One colour for the dots (user `styles.circles[k].upColor` first). */
function dotsFigure(out: Pick<OutputSpec, 'key' | 'label'>, circleIndex: number): IndicatorFigure<Row> {
  return {
    key: out.key,
    title: `${out.label}: `,
    type: 'circle',
    styles: (_data, indicator, defaultStyles) => {
      const own = indicator.styles?.circles?.[circleIndex]
      const dflt = defaultStyles.circles[circleIndex] ?? defaultStyles.circles[0]
      const color = own?.upColor ?? dflt.upColor
      return { color, borderColor: color }
    },
  }
}

export interface StyleSlot {
  /** Figure key (`hist`, `pattern_bull`). */
  key: string
  /** The def output this figure draws (a marker output has two figures). */
  outputKey: string
  /** Which colour slot of the output the figure takes; a histogram's single figure carries both (up, down). */
  role: 'line' | 'dots' | 'up' | 'down'
  slot: 'lines' | 'bars' | 'circles'
  index: number
}

/** One entry per FIGURE, in figure order. klinecharts indexes `styles.lines[k]` / `bars[k]` / `circles[k]` per figure TYPE, counted separately. */
export function styleSlotsFor(def: IndicatorDef): StyleSlot[] {
  const counts = { lines: 0, bars: 0, circles: 0 }
  const slots: StyleSlot[] = []
  for (const o of def.outputs) {
    if (o.plot === 'histogram') slots.push({ key: o.key, outputKey: o.key, role: 'up', slot: 'bars', index: counts.bars++ })
    else if (o.plot === 'dots') slots.push({ key: o.key, outputKey: o.key, role: 'dots', slot: 'circles', index: counts.circles++ })
    else if (o.plot === 'marker') {
      slots.push({ key: `${o.key}_bull`, outputKey: o.key, role: 'up', slot: 'circles', index: counts.circles++ })
      slots.push({ key: `${o.key}_bear`, outputKey: o.key, role: 'down', slot: 'circles', index: counts.circles++ })
    } else slots.push({ key: o.key, outputKey: o.key, role: 'line', slot: 'lines', index: counts.lines++ })
  }
  return slots
}

function seriesOf(def: IndicatorDef): IndicatorSeries {
  if (def.series === 'price') return IndicatorSeries.Price
  if (def.series === 'volume') return IndicatorSeries.Volume
  return IndicatorSeries.Normal
}

export function buildTemplate(def: IndicatorDef, calc: CalcFn, name: string = engineBase(def)): IndicatorTemplate<Row> {
  const counts = { bars: 0, circles: 0 }
  const figures: IndicatorFigure<Row>[] = []
  for (const o of def.outputs) {
    if (o.plot === 'histogram') figures.push(histogramFigure(o, counts.bars++))
    else if (o.plot === 'dots') figures.push(dotsFigure(o, counts.circles++))
    else if (o.plot === 'marker') {
      figures.push(dotsFigure({ key: `${o.key}_bull`, label: 'Bullish' }, counts.circles++))
      figures.push(dotsFigure({ key: `${o.key}_bear`, label: 'Bearish' }, counts.circles++))
    } else figures.push({ key: o.key, title: `${o.label}: `, type: 'line' })
  }
  return {
    name,
    shortName: def.shortLabel ?? def.id,
    series: seriesOf(def),
    ...(def.series === 'normal' && def.precision !== undefined ? { precision: def.precision } : {}),
    shouldOhlc: def.pane === 'main',
    shouldFormatBigNumber: def.series === 'volume',
    calcParams: toCalcParams(def, defaultInputs(def)),
    figures,
    ...(def.range?.min !== undefined ? { minValue: def.range.min } : {}),
    ...(def.range?.max !== undefined ? { maxValue: def.range.max } : {}),
    calc: (dataList, indicator) => toRows(calc(dataList, fromCalcParams(def, indicator.calcParams)), def.outputs, dataList),
  }
}

// ---- loading + registration -------------------------------------------------

const loading = new Map<string, Promise<CalcFn>>()
const loaded = new Map<string, CalcFn>()
const registering = new Map<string, Promise<void>>()

/** The def's calc chunk (dynamic import), memoised. Rejects for a builtin, which has none. */
export function loadCalc(def: IndicatorDef): Promise<CalcFn> {
  const key = defKey(def)
  const done = loaded.get(key)
  if (done) return Promise.resolve(done)
  let p = loading.get(key)
  if (!p) {
    const loader = calcLoaderFor(def)
    p = loader
      ? loader().then((calc) => {
          loaded.set(key, calc)
          return calc
        })
      : Promise.reject(new Error(`indicator ${key} has no calc chunk`))
    p.catch(() => loading.delete(key))
    loading.set(key, p)
  }
  return p
}

export function isRegistered(def: IndicatorDef): boolean {
  return def.builtin === true || getSupportedIndicators().includes(engineBase(def))
}

/** Load the calc chunk and register the template once. No-op for a builtin. Await this BEFORE any synchronous engine work. */
export function ensureRegistered(def: IndicatorDef): Promise<void> {
  if (isRegistered(def)) return Promise.resolve()
  const key = defKey(def)
  let p = registering.get(key)
  if (!p) {
    p = loadCalc(def)
      .then((calc) => {
        if (!getSupportedIndicators().includes(engineBase(def))) registerIndicator(buildTemplate(def, calc))
      })
      .finally(() => registering.delete(key))
    registering.set(key, p)
  }
  return p
}

/** Synchronous: register the def's template under a clone name (`RSI@2#2`). False until the base is loaded. */
export function registerClone(def: IndicatorDef, cloneName: string): boolean {
  const calc = loaded.get(defKey(def))
  if (!calc) return false
  if (!getSupportedIndicators().includes(cloneName)) registerIndicator(buildTemplate(def, calc, cloneName))
  return true
}

/** Register every known (id, version) in `refs` (unknown ones are ignored); a failed chunk is logged, not thrown. */
export async function preloadIndicators(refs: Iterable<{ name: string; version?: number }>): Promise<void> {
  const defs = new Map<string, IndicatorDef>()
  for (const r of refs) {
    const def = resolveDef(r.name, r.version ?? 1)
    if (def) defs.set(defKey(def), def)
  }
  const results = await Promise.allSettled([...defs.values()].map((def) => ensureRegistered(def)))
  results.forEach((r, i) => {
    if (r.status === 'rejected') console.error(`indicator ${[...defs.keys()][i]} failed to load`, r.reason)
  })
}
